import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ChevronRight, RefreshCw, Search } from "lucide-react";
import { useDeferredValue, useEffect, useMemo, useState } from "react";

import { HistoryFeedRow } from "@/components/history/history-feed-row";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { groupByDay } from "@/lib/history-date";
import {
  CATEGORY_TABS,
  FEED_PAGE_SIZE,
  fetchFeedPage,
  filterFeedByCategory,
  filterFeedBySearch,
  isTypeAllowed,
  sealFeed,
  sortFeedByDate,
  typeTabsFor,
  type CategoryFilter,
  type TypeFilter,
} from "@/lib/history-feed";
import { cn } from "@/lib/utils";

/**
 * Lịch sử — MỘT dòng thời gian cho mọi thứ đã xem.
 *
 * 🔴 Trước bản này trang chia làm hai nửa tách biệt (Luận giải / Đàm đạo). Cách ấy hỏng
 * ở đúng ca quan trọng nhất: gieo quẻ miễn phí xong mà số dư không đủ để luận giải thì
 * quẻ nằm trong DB với 0 tin nhắn, và người dùng mở Lịch sử ra không thấy nó ở nửa mình
 * đang đứng. Giờ mọi bản ghi nằm chung một danh sách, phân biệt bằng TAG chứ không bằng
 * việc giấu ở nửa kia.
 *
 * Ba bộ lọc, và chúng KHÔNG chạy cùng một chỗ — đọc kỹ trước khi thêm cái thứ tư:
 * - **Loại hình** (Kinh Dịch / Tử Vi / …) chọn NGUỒN để gọi ⇒ chạy ở server, đếm chính xác.
 * - **Từ khoá** đi vào `/v2/chat/sessions?q=` cho phiên trò chuyện ⇒ server; bốn nguồn
 *   còn lại không có tham số tìm kiếm nên chỉ soi được phần đã tải.
 * - **Tag** (Luận giải / Đàm đạo) suy từ số tin nhắn của từng bản ghi ⇒ chỉ chạy được ở
 *   client (xem `filterFeedByCategory`).
 *
 * ⚠️ Vì hai bộ lọc sau chạy ở client, trang CHỈ hiện tổng số khi không có bộ lọc nào
 * trong hai cái ấy. Hiện "N mục" của cả tài khoản bên cạnh một danh sách đã bị lọc lại
 * là nói dối về con số.
 */

/**
 * Số lượt tải TỐI ĐA được phép chạy tự động vượt quá trang đang xem.
 *
 * Một trang hiển thị 10 dòng không ứng với một lượt tải nào của riêng nguồn nào, nên
 * trang phải tự nạp thêm cho tới khi đủ. Con số này là cái phanh — xem chỗ dùng.
 */
const AUTOLOAD_SLACK_ROUNDS = 5;

const TYPE_FILTER_VALUES = new Set([
  "all",
  "iching",
  "tuvi",
  "bazi",
  "palm",
  "tong_hop",
  "live",
  "birth_time",
]);
const CATEGORY_FILTER_VALUES = new Set(["all", "luan-giai", "dam-dao"]);

export const Route = createFileRoute("/_authenticated/lich-su")({
  // Bộ lọc nằm trong URL để chia sẻ / đánh dấu trang và để nút Back quay lại đúng chỗ.
  //
  // ⚠️ Mọi tham số đều TUỲ CHỌN: khai một cái là bắt buộc thì mọi `<Link to="/lich-su">`
  // sẵn có trong app (thanh nav, sidebar…) đều thành lỗi biên dịch vì thiếu `search`.
  validateSearch: (
    search: Record<string, unknown>,
  ): { tag?: CategoryFilter; kind?: TypeFilter; page?: number } => ({
    // Trang cũng nằm trong URL: gửi link "trang 3" cho người khác phải ra đúng trang 3,
    // và nút Back phải quay về trang vừa rời đi chứ không nhảy về đầu danh sách.
    page:
      typeof search.page === "number" && Number.isFinite(search.page) && search.page > 1
        ? Math.floor(search.page)
        : undefined,
    tag:
      typeof search.tag === "string" && CATEGORY_FILTER_VALUES.has(search.tag)
        ? (search.tag as CategoryFilter)
        : undefined,
    kind:
      typeof search.kind === "string" && TYPE_FILTER_VALUES.has(search.kind)
        ? (search.kind as TypeFilter)
        : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Lịch Sử Tham Vấn - Quẻ Kinh Dịch & Lá Số Đã Xem" },
      {
        name: "description",
        content:
          "Xem lại các lá số Tử Vi, Bát Tự và quẻ Kinh Dịch đã gieo, tiếp tục luận giải bất cứ lúc nào. Xem ngay!",
      },
    ],
  }),
  component: LichSuPage,
});

function LichSuPage() {
  const navigate = useNavigate({ from: "/lich-su" });
  const search = Route.useSearch();
  const category: CategoryFilter = search.tag ?? "all";
  const type: TypeFilter = search.kind ?? "all";

  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const trimmedQuery = deferredQuery.trim();

  const feed = useInfiniteQuery({
    queryKey: ["history-feed", { category, type, q: trimmedQuery }],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => fetchFeedPage({ category, type, q: trimmedQuery }, pageParam),
    // Số trang đã tải + 1: mọi nguồn luôn đi CÙNG một số trang, nên chỉ cần một con đếm
    // duy nhất thay vì con trỏ riêng cho từng nguồn.
    getNextPageParam: (last, all) => (last.hasMore ? all.length + 1 : undefined),
  });

  /**
   * Toàn bộ những gì đã tải, sau khi CẮT tại ranh giới an toàn rồi mới lọc.
   *
   * 🔴 Thứ tự ba bước không đổi được: sắp → cắt (`sealFeed`) → lọc. Cắt sau khi lọc là
   * cắt nhầm chỗ; chia trang trước khi cắt là để một bản ghi chưa tải xong chen vào giữa
   * ở lượt sau và đẩy mọi thứ dịch một ô.
   */
  const visible = useMemo(() => {
    const loaded = feed.data?.pages.flatMap((entry) => entry.items) ?? [];
    // Sắp lại TOÀN BỘ, không phải từng trang — xem ghi chú đầu `history-feed.ts`.
    const sorted = sortFeedByDate(loaded);
    const sealed = sealFeed(sorted, feed.data?.pages.at(-1)?.sealedUntil ?? null);
    return filterFeedByCategory(filterFeedBySearch(sealed, trimmedQuery), category);
  }, [feed.data, category, trimmedQuery]);

  const hasClientFilter = category !== "all" || trimmedQuery.length > 0;
  const total = feed.data?.pages.at(-1)?.total ?? 0;
  const typeTabs = typeTabsFor(category);
  const hasAnyFilter = hasClientFilter || type !== "all";

  /**
   * Số trang.
   *
   * ⚠️ `null` khi đang có bộ lọc chạy ở CLIENT (tag hoặc từ khoá): lúc ấy không ai biết
   * tổng số thật, vì server đếm cả những mục bộ lọc kia sẽ loại. Hiện "Trang 2/9" trong
   * tình huống đó là bịa một con số. Thanh phân trang tự đổi sang dạng không có mẫu số.
   */
  const totalPages = hasClientFilter ? null : Math.max(1, Math.ceil(total / FEED_PAGE_SIZE));
  const page = Math.max(1, Math.min(search.page ?? 1, totalPages ?? Number.MAX_SAFE_INTEGER));

  const items = useMemo(
    () => visible.slice((page - 1) * FEED_PAGE_SIZE, page * FEED_PAGE_SIZE),
    [visible, page],
  );
  const groups = useMemo(() => groupByDay(items, (item) => item.createdAt, "newest"), [items]);

  /**
   * Nạp thêm khi trang đang xem chưa nằm gọn trong phần đã tải.
   *
   * Một "trang hiển thị" 10 dòng KHÔNG ứng với một trang của nguồn nào: nó là lát cắt của
   * danh sách gộp. Sang trang 3 có thể cần xin thêm vài lượt ở các nguồn — cứ nạp cho tới
   * khi đủ, hoặc tới khi mọi nguồn cạn.
   */
  const loadedRounds = feed.data?.pages.length ?? 0;
  const { hasNextPage: canLoadMore, isFetchingNextPage, fetchNextPage } = feed;
  useEffect(() => {
    if (visible.length >= page * FEED_PAGE_SIZE) return;
    if (!canLoadMore || isFetchingNextPage) return;
    // 🔴 Trần chống chạy hoang. Bộ lọc tag và từ khoá chạy ở client, nên một từ khoá
    // không khớp gì sẽ khiến vòng này xin hết trang này tới trang khác của MỌI nguồn —
    // hàng chục lượt gọi mạng cho một danh sách rỗng. Chạm trần thì dừng tự động; người
    // xem vẫn bấm "Sau" được, và mỗi lần bấm là trần nới thêm một nấc.
    if (loadedRounds >= page + AUTOLOAD_SLACK_ROUNDS) return;
    void fetchNextPage();
  }, [visible.length, page, loadedRounds, canLoadMore, isFetchingNextPage, fetchNextPage]);

  // Còn trang sau khi phần đã tải vượt quá trang này, hoặc khi vẫn còn nguồn chưa cạn.
  const hasNextPage = visible.length > page * FEED_PAGE_SIZE || feed.hasNextPage;

  const goToPage = (next: number) =>
    void navigate({
      search: (prev) => ({ ...prev, page: next <= 1 ? undefined : next }),
      replace: true,
    });

  // Đổi bộ lọc là đổi cả danh sách → luôn về trang 1. Không reset là người đang đứng ở
  // trang 7 của bộ lọc cũ nhìn thấy trang 7 (thường rỗng) của bộ lọc mới.
  const setFilter = (next: { tag?: CategoryFilter; kind?: TypeFilter }) =>
    void navigate({
      search: (prev) => ({
        ...prev,
        page: undefined,
        ...(next.tag !== undefined ? { tag: next.tag === "all" ? undefined : next.tag } : {}),
        ...(next.kind !== undefined ? { kind: next.kind === "all" ? undefined : next.kind } : {}),
      }),
      replace: true,
    });

  /**
   * Đổi tag.
   *
   * ⚠️ Đổi tag có thể làm bộ lọc loại hình hết nghĩa (Bói Live không bao giờ là Đàm đạo).
   * Kéo nó về "Tất cả" NGAY TẠI ĐÂY, trong cùng một lượt điều hướng, chứ không bằng một
   * effect dọn dẹp: effect chạy sau một nhịp render, tức là có đúng một nhịp danh sách
   * rỗng — và thêm một bản ghi thừa vào lịch sử trình duyệt.
   */
  const changeCategory = (nextTag: CategoryFilter) =>
    setFilter({ tag: nextTag, ...(isTypeAllowed(nextTag, type) ? {} : { kind: "all" as const }) });

  return (
    <main className="app-feature-page flex-1 overflow-y-auto px-4 py-6 pb-24 md:px-6 md:py-8 md:pb-8 lg:px-12">
      <div className="mx-auto max-w-5xl">
        <h1 className="sr-only">Lịch sử tham vấn</h1>

        <section aria-label="Bộ lọc lịch sử" className="mb-6 flex flex-col gap-3">
          <ToggleGroup
            type="single"
            value={category}
            onValueChange={(value) => value && changeCategory(value as CategoryFilter)}
            aria-label="Lọc theo loại nội dung"
            className="inline-flex w-full gap-1 rounded-full border border-gold-soft/60 bg-background/40 p-1 sm:w-auto sm:self-start"
          >
            {CATEGORY_TABS.map((tab) => (
              <ToggleGroupItem
                key={tab.key}
                value={tab.key}
                aria-label={`Hiển thị ${tab.label}`}
                className="h-9 flex-1 rounded-full px-5 font-display text-xs tracking-widest text-muted-foreground data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:shadow-gold sm:flex-none"
              >
                {tab.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>

          <div className="flex min-w-0 items-center gap-2">
            <div className="relative min-w-0 flex-1">
              <ToggleGroup
                type="single"
                value={type}
                onValueChange={(value) => value && setFilter({ kind: value as TypeFilter })}
                aria-label="Lọc theo loại hình"
                className="scrollbar-none justify-start overflow-x-auto rounded-full border border-gold-soft/60 bg-background/40 p-1 pr-10 whitespace-nowrap sm:pr-1"
              >
                {typeTabs.map((tab) => (
                  <ToggleGroupItem
                    key={tab.key}
                    value={tab.key}
                    aria-label={`Hiển thị ${tab.label}`}
                    className="h-8 shrink-0 rounded-full px-4 font-display text-xs tracking-widest text-muted-foreground data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:shadow-gold"
                  >
                    {tab.label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-px right-px flex w-10 items-center justify-end rounded-r-full bg-gradient-to-l from-background via-background/90 to-transparent pr-2 text-muted-foreground sm:hidden"
              >
                <ChevronRight />
              </div>
            </div>
            {/* Tổng số chỉ đúng khi KHÔNG có bộ lọc chạy ở client — xem ghi chú đầu file. */}
            {!hasClientFilter && (
              <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
                {total} mục
              </span>
            )}
          </div>

          <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
            <div className="relative">
              <label htmlFor="history-search" className="sr-only">
                Tìm trong lịch sử
              </label>
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                id="history-search"
                type="search"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  // Chỉ điều hướng khi thật sự đang ở trang khác 1 — không thì mỗi phím gõ
                  // là một lượt `navigate` thừa.
                  if (page > 1) goToPage(1);
                }}
                placeholder="Tìm theo chủ đề, tên lá số..."
                className="pl-9"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => feed.refetch()}
              disabled={feed.isFetching}
              aria-label={feed.isFetching ? "Đang tải lại lịch sử" : "Tải lại lịch sử"}
            >
              <RefreshCw
                data-icon="inline-start"
                className={cn(feed.isFetching && "animate-spin")}
              />
              Tải lại
            </Button>
          </div>

          <span className="sr-only" aria-live="polite">
            Đang hiển thị {items.length} mục
          </span>
        </section>

        {feed.isLoading && <FeedSkeleton />}

        {feed.error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-sm text-destructive">
            {(feed.error as Error).message}
          </div>
        )}

        {/* Hai câu cho hai tình huống khác nhau: không có bộ lọc nào ⇒ người này chưa
            từng tham vấn; có bộ lọc ⇒ chỉ là từ khoá/tab chưa trúng. Chúng mời người xem
            làm hai việc khác nhau. */}
        {!feed.isLoading && !feed.error && items.length === 0 && !hasAnyFilter && (
          <div className="rounded-lg border border-gold-soft/40 bg-background/30 p-12 text-center">
            <p className="font-serif-soft italic text-muted-foreground">
              Chưa có gì trong lịch sử. Hãy gieo quẻ, lập lá số hoặc xem chỉ tay trước.
            </p>
          </div>
        )}

        {!feed.isLoading && !feed.error && items.length === 0 && hasAnyFilter && (
          <div className="rounded-lg border border-gold-soft/40 bg-background/30 px-6 py-10 text-center">
            <p className="font-serif-soft text-base text-foreground">Không tìm thấy mục phù hợp</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {hasNextPage
                ? "Hãy sang trang sau để tìm rộng hơn, hoặc đổi bộ lọc."
                : "Hãy thử từ khoá khác hoặc chọn lại bộ lọc."}
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="mt-4"
              onClick={() => {
                setQuery("");
                setFilter({ tag: "all", kind: "all" });
              }}
            >
              Xoá bộ lọc
            </Button>
          </div>
        )}

        <div className="flex flex-col gap-6">
          {groups.map((group) => (
            <section key={group.key} aria-labelledby={`history-group-${group.key}`}>
              <div className="mb-2 flex items-center gap-3">
                <h2
                  id={`history-group-${group.key}`}
                  className="font-display text-xs uppercase tracking-[0.18em] text-foreground/80"
                >
                  {group.label}
                </h2>
                <span className="text-xs text-muted-foreground">{group.items.length}</span>
                <div className="h-px flex-1 bg-border/60" />
              </div>
              <ul className="flex flex-col gap-2">
                {group.items.map((item) => (
                  <HistoryFeedRow key={item.key} item={item} />
                ))}
              </ul>
            </section>
          ))}
        </div>

        {!feed.isLoading && !feed.error && (page > 1 || hasNextPage) && (
          <nav
            aria-label="Phân trang lịch sử"
            className="mt-6 flex items-center justify-between gap-3 text-xs text-muted-foreground"
          >
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1 || feed.isFetching}
              onClick={() => goToPage(page - 1)}
            >
              Trước
            </Button>
            <span aria-live="polite">
              {totalPages ? `Trang ${page}/${totalPages} · ${total} mục` : `Trang ${page}`}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasNextPage || feed.isFetching}
              onClick={() => goToPage(page + 1)}
            >
              Sau
            </Button>
          </nav>
        )}
      </div>
    </main>
  );
}

function FeedSkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-label="Đang tải lịch sử">
      {[...Array(5)].map((_, index) => (
        <div
          key={index}
          className="flex items-center gap-3 rounded-lg border border-gold-soft/30 bg-background/30 p-3"
        >
          <Skeleton className="size-10 shrink-0 rounded-xl" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}
